import { useInfiniteQuery } from "@tanstack/react-query";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import { Stars, formatRating } from "@/components/reviews/Stars";
import { api } from "@/lib/api";

const dateFmt = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";
};

export const VerifiedNotice = () => (
  <p className="flex items-start gap-3 rounded-2xl bg-cream p-5 text-[15px] leading-relaxed text-slate2" data-testid="reviews-verified-notice">
    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-copper" strokeWidth={1.5} />
    <span><strong className="text-navy">Recensioni verificate.</strong> Solo chi ha ricevuto un ordine Perfero può lasciare una recensione, tramite il link personale inviato via email dopo la consegna. Pubblichiamo tutte le recensioni, positive e negative; rimuoviamo solo quelle offensive o fuori tema.</span>
  </p>
);

const Summary = ({ data }) => (
  <div className="rounded-3xl border border-copper/20 bg-cream p-7" data-testid="reviews-summary">
    <div className="flex items-end gap-4">
      <p className="font-display text-6xl leading-none text-navy" data-testid="reviews-average">{formatRating(data.rating_average)}</p>
      <div className="pb-1">
        <Stars value={data.rating_average} size="h-5 w-5" />
        <p className="mt-1 text-[15px] text-slate2" data-testid="reviews-count">{data.rating_count} {data.rating_count === 1 ? "recensione" : "recensioni"}</p>
      </div>
    </div>
    <ul className="mt-6 space-y-2">
      {["5", "4", "3", "2", "1"].map((n) => {
        const count = data.distribution?.[n] ?? 0;
        return (
          <li key={n} className="flex items-center gap-3 text-[14px] text-slate2" data-testid={`reviews-bar-${n}`}>
            <span className="w-14 shrink-0">{n} stelle</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-sand">
              <span className="block h-full rounded-full bg-gold" style={{ width: `${data.rating_count ? (count / data.rating_count) * 100 : 0}%` }} />
            </span>
            <span className="w-8 shrink-0 text-right">{count}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

const ReviewItem = ({ review }) => (
  <li className="border-t border-copper/15 py-6" data-testid={`review-${review.id}`}>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <Stars value={review.rating} />
      <p className="font-medium text-navy">{review.author}</p>
      <p className="inline-flex items-center gap-1 text-[13px] text-[#166534]"><BadgeCheck className="h-4 w-4" /> Acquisto verificato</p>
    </div>
    {review.comment && <p className="mt-3 text-[17px] leading-relaxed text-ink">{review.comment}</p>}
    <p className="mt-2 text-[13px] text-slate2">{dateFmt(review.created_at)}</p>
  </li>
);

export const ReviewsSection = ({ slug }) => {
  const query = useInfiniteQuery({
    queryKey: ["reviews", slug],
    queryFn: async ({ pageParam }) => (await api.get(`/products/${slug}/reviews`, { params: { page: pageParam } })).data,
    initialPageParam: 1,
    getNextPageParam: (last) => (last?.has_more ? last.page + 1 : undefined),
  });
  const first = query.data?.pages?.[0];
  const reviews = (query.data?.pages ?? []).flatMap((p) => (Array.isArray(p?.reviews) ? p.reviews : []));
  if (query.isError || !first || !(first.rating_count > 0)) return null;

  return (
    <section id="recensioni" className="mt-28 scroll-mt-36 border-t border-copper/20 pt-20" data-testid="reviews-section">
      <p className="eyebrow">Recensioni</p>
      <h2 className="mt-4 font-display text-4xl font-medium text-navy sm:text-5xl">Cosa ne pensa chi lo usa</h2>
      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-4">
          <Summary data={first} />
          <VerifiedNotice />
        </div>
        <div className="lg:col-span-8">
          {reviews.length > 0 && <ul className="border-b border-copper/15" data-testid="reviews-list">{reviews.map((r) => <ReviewItem key={r.id} review={r} />)}</ul>}
          {query.hasNextPage && (
            <button type="button" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage} data-testid="reviews-load-more" className="mt-8 h-12 rounded-full border border-navy/20 px-6 text-[16px] text-navy transition-colors hover:bg-navy hover:text-ivory disabled:opacity-50">
              {query.isFetchingNextPage ? "Caricamento…" : "Mostra altre recensioni"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
